import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Button, Glyphicon, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isImageDataURI } from './utils';

class CredentialCard extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.credentialTypes = {
			apiKey: this.intl.formatMsg('API Key credential'),
			basic: this.intl.formatMsg('HTTP Basic credential'),
			oauth2: this.intl.formatMsg('OAuth 2.0 credential with authorization code grant'),
			oauth2ClientCredentials: this.intl.formatMsg('OAuth 2.0 credential with client credentials grant'),
			token: this.intl.formatMsg('Token credential')
		};

		// Status icons
		this.statusIcons = {
			needsAuth: 'warning icon-GG-circle-minus',
			needsBasic: 'error icon-GG-cross-circle',
			needsAPIKey: 'error icon-GG-cross-circle',
			needsClient: 'error icon-GG-cross-circle',
			needsPK: 'error icon-GG-cross-circle',
			authError: 'error icon-GG-cross-circle',
			needsRefresh: 'warning icon-GG-circle-minus',
			refreshError: 'error icon-GG-cross-circle',
			none: 'icon-GG-checkmark-circle',
			unknown: 'error icon-GG-circle-minus'
		};

		// All credential statuses that need link to our docs in the card message for detailed
		// configuration

		this.credentialStatusesWithLinkToDocs = [
			'credential.status.needsBasic',
			'credential.status.needsAPIKey',
			'credential.status.needsClient.none.oauth2',
			'credential.status.needsPK.none.oauth2',
			'credential.status.needsClient.none.basic',
			'credential.status.needsClient.none.apiKey',
			'credential.status.authError.none.oauth2'
		];

		// Links to our docs for apiKey, Basic and OAuth2 configuratin setup
		this.credentialLinksForConfigurationSetup = {
			apiKey: 'https://docs.axway.com/bundle/api-builder/page/docs/developer_guide/credentials/configuring_credentials/api_key_credentials/index.html',
			basic: 'https://docs.axway.com/bundle/api-builder/page/docs/developer_guide/credentials/configuring_credentials/http_basic_credentials/index.html',
			oauth2: {
				accessCode: 'https://docs.axway.com/bundle/api-builder/page/docs/developer_guide/credentials/configuring_credentials/oauth_2.0_credentials/index.html',
				clientCredentials: 'https://docs.axway.com/bundle/api-builder/page/docs/developer_guide/credentials/configuring_credentials/oauth_2.0_credentials/index.html'
			}
		};

		// Generic translations
		this.intl.formatMsg('View documentation.', 'credential.documentation');

		// Status ToolTip Intl
		this.intl.formatMsg('Requires Authorization', 'credential.statustip.needsAuth');
		this.intl.formatMsg('Invalid Configuration', 'credential.statustip.needsBasic');
		this.intl.formatMsg('Invalid Configuration', 'credential.statustip.needsAPIKey');
		this.intl.formatMsg('Invalid Configuration', 'credential.statustip.needsClient');
		this.intl.formatMsg('Invalid Configuration', 'credential.statustip.needsPK');
		this.intl.formatMsg('Authorization Error', 'credential.statustip.authError');
		this.intl.formatMsg('Refresh Required', 'credential.statustip.needsRefresh');
		this.intl.formatMsg('Refresh Error', 'credential.statustip.refreshError');
		this.intl.formatMsg('Ready', 'credential.statustip.none');
		this.intl.formatMsg('Unknown', 'credential.statustip.unknown');

		// Status Message Intl search order is:
		// credential.status.<action>.<credential status>.<credential status>`
		// credential.status.<action>.<credential status>`
		// credential.status.<action>`
		// credential.status.default`

		// Configuration errors
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until the username and password are configured.', 'credential.status.needsBasic');
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until the API key is configured.', 'credential.status.needsAPIKey');
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until the client id and secret are configured.', 'credential.status.needsClient.none.oauth2');
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until client id, private key and passphrase are configured.', 'credential.status.needsPK.none.oauth2');
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until the username and password are configured.', 'credential.status.needsClient.none.basic');
		this.intl.formatMsg('The credential configuration is invalid and the credential cannot be used until the API key is configured.', 'credential.status.needsClient.none.apiKey');
		this.intl.formatMsg('The credential configuration is invalid and there was an error in the user authorization.', 'credential.status.authError.none.oauth2');

		// Needs manual authorization
		this.intl.formatMsg('The credential is unauthorized and cannot be used until it is authorized.', 'credential.status.needsAuth');
		this.intl.formatMsg('The credential is expired and cannot be used until it is authorized.', 'credential.status.needsAuth.expired');

		// Authorization error
		this.intl.formatMsg('The credential is unauthorized and there was an error performing authorization.', 'credential.status.authError');
		this.intl.formatMsg('The credential is currently valid but there was an error performing re-authorization.', 'credential.status.authError.ok');
		this.intl.formatMsg('The credential is expired and there was an error performing authorization.', 'credential.status.authError.expired');

		// Refresh required
		this.intl.formatMsg('The credential is unauthorized and will be only be usable after it has been refreshed.', 'credential.status.needsRefresh');
		this.intl.formatMsg('The credential is expired and will be only be usable after it has been refreshed.', 'credential.status.needsRefresh.expired');

		// Refresh error
		this.intl.formatMsg('The credential is unauthorized and cannot be used until it is authorized.', 'credential.status.refreshError');
		this.intl.formatMsg('The credential is expired and cannot be used until it is authorized.', 'credential.status.refreshError.expired');
		this.intl.formatMsg('The credential could not be refreshed and will be unusable after it expires.', 'credential.status.refreshError.ok');

		// All good
		this.intl.formatMsg('The credential is authorized and ready to be used.', 'credential.status.none.ok');
		this.intl.formatMsg('The credential is authorized and is currently valid.', 'credential.status.none.ok.oauth2');

		// Default error message
		this.intl.formatMsg('The credential is not valid and cannot be used.', 'credential.status.default');
	}

	/**
	 * Get the internationalized messages by id.
	 */
	getMessage(msgId) {
		const { intl } = this.props;
		return intl.formatMessage({ id: msgId, defaultMessage: ' ' }).trim();
	}

	/**
	 * Get the icon to display for this credential.
	 */
	icon() {
		const { credential } = this.props;
		let icon;
		if (credential.icon) {
			if (isImageDataURI(credential.icon)) {
				icon = (
					<img src={credential.icon} alt="" />
				);
			} else {
				icon = (
					<Glyphicon glyph={` gg ${credential.icon}`} />
				);
			}
		}
		return icon;
	}

	/**
	 * The list of actions that can be performed on the credential.
	 */
	renderActions(actions) {
		return (actions || []).map((action) => (
			<div
				className="action"
				key={action.label}
			>
				<Button
					key={action.label}
					className="action-btn"
					bsStyle="link"
					onClick={action.action}
				>
					<span>{action.label}</span>
				</Button>
			</div>
		));
	}

	/**
	 * The status icon.
	 */
	statusIcon() {
		const { credential } = this.props;
		let status;
		if (credential.status) {
			status = (<Glyphicon glyph={` gg ${this.statusIcons[credential.status.action] || this.statusIcons.unknown}`} />);
		}
		return status;
	}

	/**
	 * The status tooltip.
	 */
	statusTooltip() {
		const { credential } = this.props;
		let msg;
		if (credential.status) {
			msg = this.getMessage(`credential.statustip.${credential.status.action}`);
		}
		if (!msg) {
			msg = this.getMessage('credential.statustip.unknown');
		}
		return (
			<Tooltip id="status-tooltip">{msg}</Tooltip>
		);
	}

	/**
	 * Return the possible status keys for this credential.
	 */
	*getStatusMessageKey(base, credential) {
		const type = credential.type;
		const action = (credential.status && credential.status.action) || 'default';
		const cred = (credential.status && credential.status.credential) || 'default';

		yield `${base}.${action}.${cred}.${type}`;
		yield `${base}.${action}.${cred}`;
		yield `${base}.${action}`;
		yield `${base}.default`;
		return;
	}

	/**
	 * Returns an obejct of message and key.
	 */
	statusText() {
		const { credential } = this.props;
		const base = 'credential.status';
		const statusMessage = {};

		for (const key of this.getStatusMessageKey(base, credential)) {
			statusMessage.message = this.getMessage(key);
			if (statusMessage.message) {
				statusMessage.key = key;
				break;
			}
		}
		return statusMessage;
	}

	credentialType() {
		const { credential } = this.props;
		// oauth2 type has additional flow property. We want to display the
		// proper one so check that property first.
		if (credential.flow === 'clientCredentials') {
			return this.credentialTypes.oauth2ClientCredentials;
		}
		return this.credentialTypes[credential.type] || this.credentialTypes.token;
	}

	render() {
		const { credential } = this.props;
		const actions = this.renderActions(this.props.actions);
		const tokens = this.renderActions(this.props.tokensActions);
		const statusTooltip = this.statusTooltip();
		const statusIcon = this.statusIcon();
		let { message, key } = this.statusText();
		let docLink = this.credentialLinksForConfigurationSetup[credential.type];
		if (credential.type === 'oauth2') {
			docLink = docLink[credential.flow] || docLink.accessCode;
		}
		if (this.credentialStatusesWithLinkToDocs.indexOf(key) > -1) {
			message = (<FormattedMessage
				id="links-documentation"
				description="Detailed credential configuration setup"
				defaultMessage={message + ' {documentation}'}
				values={{
					documentation: (
						<a
							target="_blank"
							rel="noopener noreferrer"
							href={docLink}
						>
							{this.getMessage('credential.documentation')}
						</a>)
				}}
			/>);
		}

		return (
			<div className="credential-card">
				<div className="name" title={credential.name}>{credential.name}</div>
				<div className="icon">{this.icon()}</div>
				<div className="actions">
					{actions.length ? (
						<div className="action">{actions}</div>
					) : null}
					{tokens.length ? (
						<div className="tokens">{tokens}</div>
					) : null}
				</div>
				<div className="type">{this.credentialType()}</div>
				<div className="status-icon">
					{statusIcon && (
						<OverlayTrigger placement="left" overlay={statusTooltip}>
							{statusIcon}
						</OverlayTrigger>
					)}
				</div>
				<div className="status-text">{message}</div>
			</div>
		);
	}
}

CredentialCard.propTypes = {
	/**
	 * The credentials configured on the service.
	 */
	credential: PropTypes.shape({
		type: PropTypes.string,
		name: PropTypes.string.isRequired,
		icon: PropTypes.string,
		status: PropTypes.shape({
			credential: PropTypes.string,
			action: PropTypes.string
		}).isRequired
	}).isRequired,

	/**
	 * The actions available for this card.
	 */
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			action: PropTypes.func.isRequired
		})
	)
};

export default injectIntl(CredentialCard);
